/**
 * How planners decided, what followed, and the frozen monthly pack — the pure half
 * (bata-ui#166 W5-7/W5-8, bata-platform ADR 9015/9016, `docs/adr/3002`).
 *
 * Two platform reads, both served verbatim and neither computed here:
 *
 *   `GET /programme/override-outcomes`        per decision class over the governed window: decided,
 *                                             overridden, deferred, the rate, the pairs with an
 *                                             archived projection, and the realised outcome's state
 *   `GET /programme/override-outcomes/pairs`  each decided line beside its projection
 *   `GET /programme/reports/monthly`          the pack `programme.monthly_report` froze for one closed
 *                                             month; 404 `programme.report_not_frozen` until then
 *
 * The rules this module pins:
 *
 * - **The window is governed, with no default.** `windowStatus: not_set` says the registry key the
 *   owner must set; no window is assumed on the owner's behalf.
 * - **A realised outcome is never a number while it is not defined.** Every class publishes
 *   `realisedMeasure: null` today with `realised_measure_not_defined`; the screen says "not yet
 *   measurable: realised measure not defined" and draws no figure.
 * - **A rate with nothing decided is absent** (`nothing_decided`), never 0 %.
 * - **The pack is what was frozen.** The browser recomputes nothing; a total is the platform's
 *   `recommended` beside its `expected` (approved before the close, DEC-19), per family and
 *   currency, never summed across currencies.
 * - **The frozen months are one read** (`GET /programme/reports/monthly/months`, bata-platform ADR
 *   9031): the screen lists the last {@link PACK_MONTHS_PROBED} closed months and marks the ones the
 *   read names as frozen; a month it does not name is "not frozen yet", and is never requested (it
 *   used to be asked for one by one and answered 404 `report_not_frozen`).
 */

import { ApiProblemError } from '@/api';
import type { MonthlyReportMonth, OverrideOutcomes } from '@/api/generated';

import { COUNTRY_PARAM } from './control';

export { COUNTRY_PARAM };

/** The registry key the owner sets before the override read says anything (ADR 9015). */
export const OVERRIDE_WINDOW_KEY = 'programme.override_outcome.window_days';

// ── override outcomes ───────────────────────────────────────────────────────

/** The five decision classes whose trails exist. A class this build does not know reads verbatim. */
const DECISION_CLASS_KEY: Readonly<Record<string, string>> = {
  'inseason.proposal': 'strategic.overrides.class.proposal',
  'inseason.transfer': 'strategic.overrides.class.transfer',
  'inseason.price_step': 'strategic.overrides.class.priceStep',
  'exit.consolidation_lane': 'strategic.overrides.class.exitLane',
  'exit.cadence': 'strategic.overrides.class.exitCadence',
};

/** The catalogue key for a class, or `null` — the caller shows the code itself. */
export function decisionClassKey(code: string): string | null {
  return DECISION_CLASS_KEY[code] ?? null;
}

const REALISED_ABSENT_KEY: Readonly<Record<string, string>> = {
  realised_measure_not_defined: 'strategic.overrides.realised.notDefined',
  outcome_not_yet_observable: 'strategic.overrides.realised.notYetObservable',
};

/** Why a class or a pair has no realised figure. An unknown code is still an absence, stated. */
export function realisedAbsentKey(code: string | null | undefined): string {
  if (code === null || code === undefined || code === '') return 'strategic.overrides.realised.unstated';
  return REALISED_ABSENT_KEY[code] ?? 'strategic.overrides.realised.unrecognised';
}

const RATE_ABSENT_KEY: Readonly<Record<string, string>> = {
  nothing_decided: 'strategic.overrides.rate.nothingDecided',
};

export function rateAbsentKey(code: string | null | undefined): string {
  if (code === null || code === undefined || code === '') return 'strategic.overrides.rate.unstated';
  return RATE_ABSENT_KEY[code] ?? 'strategic.overrides.rate.unrecognised';
}

/** The window the read covered, or why there is none. */
export type WindowStatement =
  | { readonly kind: 'set'; readonly days: number; readonly start: string | null; readonly asOf: string }
  | { readonly kind: 'notSet'; readonly key: string }
  | { readonly kind: 'unrecognised'; readonly status: string };

export function windowStatement(outcomes: Pick<OverrideOutcomes, 'windowStatus' | 'windowDays' | 'windowStart' | 'asOf'>): WindowStatement {
  if (outcomes.windowStatus === 'not_set') return { kind: 'notSet', key: OVERRIDE_WINDOW_KEY };
  if (outcomes.windowStatus === 'set' && outcomes.windowDays !== null) {
    return { kind: 'set', days: outcomes.windowDays, start: outcomes.windowStart, asOf: outcomes.asOf };
  }
  return { kind: 'unrecognised', status: outcomes.windowStatus };
}

/** Today's UTC date — the override read's default `as_of`, stated on screen, changeable. */
export function todayUtc(now: Date): string {
  return now.toISOString().slice(0, 10);
}

export const AS_OF_PARAM = 'asOf';
export const CLASS_PARAM = 'class';

/** A plain `YYYY-MM-DD` date, or `null`. */
export function readIsoDate(value: string | null): string | null {
  if (value === null) return null;
  const trimmed = value.trim();
  return /^\d{4}-\d{2}-\d{2}$/.test(trimmed) ? trimmed : null;
}

// ── the monthly pack ────────────────────────────────────────────────────────

export const MONTH_PARAM = 'month';

/** How many closed months the page lists, and how many frozen months it asks the platform for (newest first). */
export const PACK_MONTHS_PROBED = 12;

export const REPORT_NOT_FROZEN = 'programme.report_not_frozen';

/** A 404 `report_not_frozen`: the month is not frozen yet (or the market is outside the scope). */
export function isNotFrozen(error: unknown): boolean {
  return error instanceof ApiProblemError && error.status === 404 && error.code === REPORT_NOT_FROZEN;
}

export interface PackScope {
  readonly country: string | null;
  readonly month: number | null;
}

/** `YYYYMM` as an integer, or `null` — a month number between 01 and 12 only. */
export function parseMonth(value: string | null): number | null {
  if (value === null) return null;
  const trimmed = value.trim();
  if (!/^\d{6}$/.test(trimmed)) return null;
  const month = Number(trimmed.slice(4));
  return month >= 1 && month <= 12 ? Number(trimmed) : null;
}

export function readPackScope(search: string): PackScope {
  const params = new URLSearchParams(search);
  const country = params.get(COUNTRY_PARAM)?.trim() ?? '';
  return { country: country === '' ? null : country, month: parseMonth(params.get(MONTH_PARAM)) };
}

/** One closed month and when its pack was frozen; `null` where the platform has none for it. */
export interface PackMonthRow {
  readonly month: number;
  readonly frozenAt: string | null;
}

/**
 * The last `count` closed months, each with the instant its pack was frozen where the months read
 * (`GET /programme/reports/monthly/months`) names it, else `null` — not frozen yet, and never asked
 * for. Newest first. Pure.
 */
export function packMonthRows(frozen: readonly MonthlyReportMonth[], now: Date, count: number): readonly PackMonthRow[] {
  const frozenAt = new Map(frozen.map((entry) => [entry.month, entry.frozenAt]));
  return closedMonths(now, count).map((month) => ({ month, frozenAt: frozenAt.get(month) ?? null }));
}

/**
 * The last `count` CLOSED months before `now`'s UTC month, newest first, as `YYYYMM`. The current
 * month is never offered: the job freezes a month only once it has closed.
 */
export function closedMonths(now: Date, count: number): readonly number[] {
  const months: number[] = [];
  let year = now.getUTCFullYear();
  let month = now.getUTCMonth() + 1;
  for (let index = 0; index < count; index += 1) {
    month -= 1;
    if (month === 0) {
      month = 12;
      year -= 1;
    }
    months.push(year * 100 + month);
  }
  return months;
}

/** `YYYYMM` as the reader's month and year ("September 2026"). */
export function monthLabel(yyyymm: number, locale: string): string {
  const year = Math.floor(yyyymm / 100);
  const month = yyyymm % 100;
  return new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric', timeZone: 'UTC' })
    .format(new Date(Date.UTC(year, month - 1, 1)));
}

/** The first and last day of a `YYYYMM`, ISO-8601 — the export's time window. */
export function monthWindow(yyyymm: number): { readonly from: string; readonly to: string } {
  const year = Math.floor(yyyymm / 100);
  const month = yyyymm % 100;
  const last = new Date(Date.UTC(year, month, 0)).getUTCDate();
  const mm = String(month).padStart(2, '0');
  return { from: `${year}-${mm}-01`, to: `${year}-${mm}-${String(last).padStart(2, '0')}` };
}

const FAMILY_KEY: Readonly<Record<string, string>> = {
  replenishment: 'strategic.pack.family.replenishment',
  transfer: 'strategic.pack.family.transfer',
  markdown: 'strategic.pack.family.markdown',
  exit_consolidation: 'strategic.pack.family.exitConsolidation',
};

export function familyKey(family: string): string | null {
  return FAMILY_KEY[family] ?? null;
}

/** The pack's caveats cell (keys joined by `;`) as a list, in order, duplicates dropped. */
export function caveatList(caveats: string): readonly string[] {
  return [...new Set(caveats.split(';').map((caveat) => caveat.trim()).filter((caveat) => caveat !== ''))];
}
